import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { getPosCountersCached } from '../utils/lookupCache';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { colors, spacing, borderRadius, shadows, typography } from '../theme/colors';
import { media, layout } from '../theme/breakpoints';
import { useIsMobile } from '../hooks/useMediaQuery';
import {
  FiLogOut, FiShoppingCart, FiDollarSign,
  FiHome, FiBarChart2, FiSettings,
  FiChevronRight, FiChevronDown, FiBook, FiDatabase,
  FiBox, FiDownload, FiUpload, FiMonitor,
  FiMenu, FiX, FiMessageCircle, FiStar, FiBell, FiAlertTriangle
} from 'react-icons/fi';
import { AnimatePresence, motion } from 'framer-motion';
import AIAssistant from './AIAssistant';
import WhatsAppStatusBadge from './WhatsAppStatusBadge';
import GlobalBusyOverlay from './GlobalBusyOverlay';
import { openAI } from '../utils/aiBus';
import SwitcherLogo from '../LOGO-SHORT.png';
import useUsageTracker from '../hooks/useUsageTracker';
import MySubscriptionDrawer from './MySubscriptionDrawer';
import SubscriptionFeaturesImg from '../Subscription Features.jpg.jpeg';

const WHATSAPP_NUMBER = '923242419744';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I am interested in subscribing to AMAL ERP. Please share the details.')}`;
// Support number for the trial-expired popup (0335-3058349).
const SUPPORT_WA_URL = `https://wa.me/923353058349?text=${encodeURIComponent('Hi, my AMAL ERP trial has expired. I want to subscribe.')}`;

const SubscriptionTag = ({ company, onOpen }) => {
  if (!company) return null;
  const sub = company.subscription;
  const plan = sub?.plan || 'trial';
  const now = new Date();
  // When onOpen is provided (owner/admin), the status chip opens the details drawer.
  const clickProps = onOpen ? { onClick: onOpen, style: { cursor: 'pointer' }, title: 'View my subscription & features' } : {};

  if (plan === 'trial') {
    const trialEnd = sub?.trialEndDate
      ? new Date(sub.trialEndDate)
      : new Date(new Date(company.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    if (daysLeft <= 0) return (
      <SubTagRow>
        <SubTag $type="expired" {...clickProps}>Trial Expired</SubTag>
        <WhatsAppBadge href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          <FiMessageCircle size={12} />
          Subscribe
        </WhatsAppBadge>
      </SubTagRow>
    );
    return (
      <SubTagRow>
        <SubTag $type="trial" {...clickProps}>Trial — {daysLeft}d left</SubTag>
        <WhatsAppBadge href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          <FiMessageCircle size={12} />
          Subscribe
        </WhatsAppBadge>
      </SubTagRow>
    );
  }

  if (sub?.nextPaymentDate && new Date(sub.nextPaymentDate) < now) {
    return (
      <SubTagRow>
        <SubTag $type="expired" {...clickProps}>Expired</SubTag>
        <WhatsAppBadge href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
          <FiMessageCircle size={12} />
          Subscribe
        </WhatsAppBadge>
      </SubTagRow>
    );
  }

  return <SubTag $type="active" {...clickProps}>Active</SubTag>;
};

const PageLayout = ({ children, pageTitle, breadcrumb, headerActions }) => {
  const { userProfile, activeCompany, companyUser, currentUser, isModuleEnabled, canViewRoute, activeBranch, branches, switchBranch, switchCompany, isBranchModuleEnabled, logout, hasPermission } = useAuth();
  // Ask AI answers from ALL company data — only owner / admin or a role with
  // Users & Roles → AI Assistant → Ask AI ticked (server checks the same).
  const canAskAI = hasPermission ? hasPermission('ai', 'askAI', 'toggle') : false;
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  // Track page views per company per day
  useUsageTracker();

  // Sidebar open/close
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  // Click-based flyout state (desktop)
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [openSubMenuIndex, setOpenSubMenuIndex] = useState(null);

  // Accordion state (mobile sidebar)
  const [expandedMenu, setExpandedMenu] = useState(null);
  const [expandedSubMenu, setExpandedSubMenu] = useState(null);

  // Trial popup state: 'features' -> null
  const [trialPopup, setTrialPopup] = useState(null);
  // My-subscription details drawer (owner/admin only).
  const [showSubDrawer, setShowSubDrawer] = useState(false);
  const canSeeSubscription = companyUser?.role === 'admin' || activeCompany?.ownerId === currentUser?.uid;
  // Trial-EXPIRED popup (shown to users whose trial has lapsed but who can still log in)
  const [expiredPopup, setExpiredPopup] = useState(false);

  useEffect(() => {
    if (!activeCompany) return;
    const sub = activeCompany.subscription;
    const plan = sub?.plan || 'trial';
    if (plan !== 'trial') return;

    const trialEnd = sub?.trialEndDate
      ? new Date(sub.trialEndDate)
      : new Date(new Date(activeCompany.createdAt).getTime() + 7 * 24 * 60 * 60 * 1000);
    const expired = (trialEnd - new Date()) <= 0;

    // Expired trial → show the expired popup (takes precedence over the features
    // popup). 2026-10-30: on EVERY login and every time the software is opened in
    // a new tab / browser — the key carries this sign-in's time, so a new login
    // shows it again; moving between pages in the same session does not.
    if (expired) {
      const signIn = currentUser?.metadata?.lastSignInTime || '';
      const key = `trialExpiredShown_${activeCompany.id}_${signIn}`;
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, 'true');
      } catch (_) { /* storage blocked — still show it */ }
      setExpiredPopup(true);
      return;
    }
    const key = `trialPopupShown_${activeCompany.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, 'true');
    setTrialPopup('features');
  }, [activeCompany, currentUser?.metadata?.lastSignInTime]);

  const handleCloseTrialPopup = () => {
    setTrialPopup(null);
  };

  const sidebarRef = useRef(null);
  const menuGroupRefs = useRef([]);

  // ── POS counters for the dynamic Sales > POS menu ──
  // Each accessible counter becomes its own menu entry (admins/owner see all;
  // cashiers see only counters they are assigned to). No counter = no POS entry.
  const [posCounters, setPosCounters] = useState([]);
  const posSaleEnabled = isModuleEnabled('pos_counter_sale');
  useEffect(() => {
    if (!activeCompany?.id || !posSaleEnabled) { setPosCounters([]); return; }
    let cancelled = false;
    (async () => {
      try {
        // Tier 6 (Firebase cost): cached — PageLayout wraps every page, so this
        // used to re-read posCounters on every navigation.
        const rows = await getPosCountersCached(activeCompany.id);
        if (cancelled) return;
        setPosCounters(rows.filter(c => c.isActive !== false));
      } catch (e) {
        if (!cancelled) setPosCounters([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeCompany, posSaleEnabled]);

  const seeAllCounters = companyUser?.role === 'admin'
    || (!!activeCompany?.ownerId && !!currentUser?.uid && activeCompany.ownerId === currentUser.uid);
  const canViewPOS = posSaleEnabled && canViewRoute('/pos');
  const posCounterMenuItems = canViewPOS
    ? posCounters
        .filter(c => seeAllCounters || (Array.isArray(c.assignedUserIds) && c.assignedUserIds.includes(currentUser?.uid)))
        .map(c => ({
          name: c.counterName || c.counterCode || 'POS',
          path: `/pos?counter=${c.id}`,
          moduleId: 'pos_counter_sale'
        }))
    : [];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const closeAllMenus = useCallback(() => {
    setOpenMenuIndex(null);
    setOpenSubMenuIndex(null);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
    setExpandedMenu(null);
    setExpandedSubMenu(null);
  }, []);

  // Central navigation. EVERY screen — POS included — navigates in the SAME tab.
  // (POS used to force a new tab; the owner wants it to behave like every other
  // menu item. Ctrl/Cmd/middle-click on a menu anchor still opens a new tab.)
  const goTo = useCallback((path) => {
    if (!path) return;
    navigate(path);
  }, [navigate]);

  const handleMenuGroupClick = useCallback((index, item) => {
    if (isMobile) {
      // Accordion behavior on mobile
      if (item.submenu) {
        setExpandedMenu(prev => prev === index ? null : index);
        setExpandedSubMenu(null);
      } else {
        goTo(item.path);
        closeSidebar();
      }
    } else {
      // Flyout behavior on desktop
      if (item.submenu) {
        setOpenMenuIndex(prev => prev === index ? null : index);
        setOpenSubMenuIndex(null);
      } else {
        goTo(item.path);
        closeAllMenus();
      }
    }
  }, [goTo, closeAllMenus, closeSidebar, isMobile]);

  const handleSubTriggerClick = useCallback((subIndex, e) => {
    e.stopPropagation();
    if (isMobile) {
      setExpandedSubMenu(prev => prev === subIndex ? null : subIndex);
    } else {
      setOpenSubMenuIndex(prev => prev === subIndex ? null : subIndex);
    }
  }, [isMobile]);

  const handleFlyoutItemClick = useCallback((path, e) => {
    // Let the browser handle Ctrl/Cmd/Shift/middle-click — these open in new tab/window.
    // Only intercept a plain left-click to perform client-side navigation.
    if (e && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) {
      return; // let the anchor's default behaviour take over
    }
    if (e) { e.preventDefault(); e.stopPropagation(); }
    goTo(path);
    if (isMobile) {
      closeSidebar();
    } else {
      closeAllMenus();
    }
  }, [goTo, closeAllMenus, closeSidebar, isMobile]);

  // Click-outside detection (desktop flyouts)
  useEffect(() => {
    if (isMobile) return;
    const handleMouseDown = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        closeAllMenus();
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [closeAllMenus, isMobile]);

  // Close menus on route change (close sidebar only on mobile)
  useEffect(() => {
    closeAllMenus();
    if (isMobile) closeSidebar();
  }, [location.pathname, closeAllMenus, closeSidebar, isMobile]);

  // Sync sidebar state when switching between mobile/desktop
  useEffect(() => {
    setSidebarOpen(!isMobile);
  }, [isMobile]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobile, sidebarOpen]);

  // L1 flyout positioning (desktop only)
  useEffect(() => {
    if (isMobile || openMenuIndex === null) return;
    const group = menuGroupRefs.current[openMenuIndex];
    if (!group) return;
    const flyout = group.querySelector('[data-flyout]');
    if (!flyout) return;
    const rect = group.getBoundingClientRect();
    const flyoutHeight = flyout.scrollHeight;
    const viewportH = window.innerHeight;
    let top = rect.top;
    if (top + flyoutHeight > viewportH - 8) {
      top = Math.max(8, viewportH - flyoutHeight - 8);
    }
    flyout.style.top = `${top}px`;
  }, [openMenuIndex, isMobile]);

  // L2 sub-flyout positioning (desktop only)
  useEffect(() => {
    if (isMobile || openSubMenuIndex === null || openMenuIndex === null) return;
    const group = menuGroupRefs.current[openMenuIndex];
    if (!group) return;
    const flyout = group.querySelector('[data-flyout]');
    if (!flyout) return;
    const subTriggers = group.querySelectorAll('[data-sub-trigger]');
    const trigger = subTriggers[openSubMenuIndex];
    if (!trigger) return;
    const subFlyout = trigger.querySelector('[data-sub-flyout]');
    if (!subFlyout) return;
    const flyoutRect = flyout.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const subHeight = subFlyout.scrollHeight;
    const viewportH = window.innerHeight;
    subFlyout.style.left = `${flyoutRect.right}px`;
    let top = triggerRect.top;
    if (top + subHeight > viewportH - 8) {
      top = Math.max(8, viewportH - subHeight - 8);
    }
    subFlyout.style.top = `${top}px`;
  }, [openSubMenuIndex, openMenuIndex, isMobile]);

  const menuItems = [
    { name: 'Dashboard', icon: FiHome, path: '/dashboard', moduleId: 'dashboard' },
    { name: 'AI Studio', icon: FiUpload, path: '/ai-import', moduleId: 'ai_import' },
    {
      name: 'Settings',
      icon: FiSettings,
      submenu: [
        { name: 'My Company', path: '/settings/company', moduleId: 'settings_company' },
        { name: 'Users & Roles', path: '/settings/users', moduleId: 'settings_users' },
        { name: 'Financial Year', path: '/settings/financial-year', moduleId: 'settings_financial_year' },
        { name: 'Document Settings', path: '/settings/documents', moduleId: 'settings_documents' },
        { name: 'Print Formats', path: '/settings/print-formats', moduleId: 'settings_print_formats' },
        { name: 'Branch Setup', path: '/settings/branches', moduleId: 'settings_branches' },
        { name: 'POS Counters', path: '/settings/pos-counters', moduleId: 'settings_pos_counters' },
        { name: 'Inventory Movement', path: '/settings/inventory-movement', moduleId: 'settings_inventory_movement' },
        { name: 'Backup', path: '/settings/backup', moduleId: 'settings_backup', adminOnly: true },
      ]
    },
    {
      name: 'Maintain',
      icon: FiDatabase,
      submenu: [
        { name: 'Chart of Accounts', path: '/master/chart-of-accounts', moduleId: 'master_chart_of_accounts' },
        { name: 'Customers', path: '/master/customers', moduleId: 'master_customers' },
        { name: 'Vendors', path: '/master/vendors', moduleId: 'master_vendors' },
        { name: 'Banks', path: '/master/banks', moduleId: 'master_banks' },
        { name: 'Items/Products', path: '/master/chart-of-inventory', moduleId: 'master_chart_of_inventory' },
        { name: 'Bulk Item Update', path: '/master/bulk-item-update', moduleId: 'maintain_bulk_item_update' },
        { name: 'Inventory Locations', path: '/master/inventory-locations', moduleId: 'master_inventory_locations' },
        { name: 'Employees', path: '/master/employees', moduleId: 'master_employees' },
        { name: 'Regions', path: '/master/regions', moduleId: 'master_regions' },
        { name: 'Jobs / Projects', path: '/master/jobs', moduleId: 'master_jobs' },
        { name: 'Opening Balances', path: '/master/opening-balances', moduleId: 'master_opening_balances' },
        { name: 'Import Charge Templates', path: '/master/import-charge-templates', moduleId: 'master_import_charge_templates' },
        { name: 'Units of Measure', path: '/master/units', moduleId: 'master_units' },
        { name: 'Barcode Labels', path: '/inventory/labels', moduleId: 'master_barcode_labels' },
        { name: 'Bank Discounts', path: '/master/bank-discounts', moduleId: 'pos_bank_discounts' },
      ]
    },
    {
      name: 'Purchases',
      icon: FiShoppingCart,
      submenu: [
        { name: 'Purchase Order', path: '/purchases/purchase-order', moduleId: 'purchases_purchase_order' },
        { name: 'Goods Receipt (GRN)', path: '/purchases/grn', moduleId: 'purchases_grn' },
        { name: 'Purchase Invoice', path: '/purchases/purchase-invoice', moduleId: 'purchases_purchase_invoice' },
        { name: 'Purchase Return', path: '/purchases/purchase-return', moduleId: 'purchases_purchase_return' },
        { name: 'Non-Tax Purchase Invoice', path: '/purchases/non-tax-invoice', moduleId: 'purchases_non_tax_invoice' },
        { name: 'Non-Tax Purchase Return', path: '/purchases/non-tax-purchase-return', moduleId: 'purchases_non_tax_return' },
        { name: 'Import Purchase Invoice', path: '/purchases/import-invoice', moduleId: 'purchases_import_invoice' },
      ]
    },
    {
      name: 'Sales',
      icon: FiDollarSign,
      submenu: [
        { name: 'Quotation', path: '/sales/quotation', moduleId: 'sales_quotation' },
        { name: 'Sale Order', path: '/sales/sale-order', moduleId: 'sales_sale_order' },
        { name: 'Delivery Challan', path: '/sales/delivery-challan', moduleId: 'sales_delivery_challan' },
        { name: 'Sale Invoice', path: '/sales/sale-invoice', moduleId: 'sales_sale_invoice' },
        { name: 'Sale Return', path: '/sales/sale-return', moduleId: 'sales_sale_return' },
        { name: 'Non-Tax Sale Invoice', path: '/sales/non-tax-invoice', moduleId: 'sales_non_tax_invoice' },
        { name: 'Non-Tax Sale Return', path: '/sales/non-tax-sale-return', moduleId: 'sales_non_tax_return' },
        ...posCounterMenuItems,
      ]
    },
    {
      name: 'Store',
      icon: FiBox,
      submenu: [
        { name: 'Purchase Requisition', path: '/store/purchase-requisition', moduleId: 'store_purchase_requisition' },
        { name: 'Inward Gate Pass', path: '/store/inward-gate-pass', moduleId: 'store_inward_gate_pass' },
        { name: 'Outward Gate Pass', path: '/store/outward-gate-pass', moduleId: 'store_outward_gate_pass' },
        { name: 'Bill of Materials', path: '/store/bill-of-materials', moduleId: 'store_bill_of_materials' },
        { name: 'Production Order', path: '/store/production-order', moduleId: 'store_production_order' },
        { name: 'Material Issue to Production', path: '/store/material-issue', moduleId: 'store_material_issue' },
        { name: 'Production Receipt', path: '/store/production-receipt', moduleId: 'store_production_receipt' },
        { name: 'Add Stock', path: '/store/add-stock', moduleId: 'store_add_stock' },
        { name: 'Reduce Stock', path: '/store/reduce-stock', moduleId: 'store_reduce_stock' },
        { name: 'Stock Audit', path: '/store/stock-audit', moduleId: 'store_stock_audit' },
        { name: 'Stock Transfer', path: '/store/stock-transfer', moduleId: 'store_stock_transfer' },
        { name: 'Branch Transfer', path: '/store/branch-transfer', moduleId: 'store_branch_transfer' },
        { name: 'Send Inventory', path: '/store/send-inventory', moduleId: 'store_send_inventory' },
        { name: 'Receive Inventory', path: '/store/receive-inventory', moduleId: 'store_receive_inventory' },
      ]
    },
    {
      name: 'Receipts',
      icon: FiDownload,
      submenu: [
        { name: 'Cash Receipt', path: '/receipts/cash-receipt', moduleId: 'receipts_cash_receipt' },
        { name: 'Bank Receipt', path: '/receipts/bank-receipt', moduleId: 'receipts_bank_receipt' },
      ]
    },
    {
      name: 'Payments',
      icon: FiUpload,
      submenu: [
        { name: 'Cash Payment', path: '/payments/cash-payment', moduleId: 'payments_cash_payment' },
        { name: 'Bank Payment', path: '/payments/bank-payment', moduleId: 'payments_bank_payment' },
      ]
    },
    {
      name: 'Journal',
      icon: FiBook,
      submenu: [
        { name: 'General Journal', path: '/journal/general', moduleId: 'journal_general' },
        { name: 'Cash Book', path: '/journal/cash-book', moduleId: 'cash_book' },
      ]
    },
    {
      name: 'Salary',
      icon: FiMonitor,
      submenu: [
        { name: 'Salary Staff', path: '/salary/staff', moduleId: 'salary_staff' },
        { name: 'Advance', path: '/salary/advance', moduleId: 'salary_advance' },
        { name: 'Loan', path: '/salary/loan', moduleId: 'salary_loan' },
        { name: 'Loan Recovery', path: '/salary/loan-recovery', moduleId: 'salary_loan_recovery' },
        { name: 'Payroll Run', path: '/salary/payroll-run', moduleId: 'salary_payroll_run' },
        { name: 'Final Settlement', path: '/salary/final-settlement', moduleId: 'salary_final_settlement' },
        { name: 'Attendance', path: '/salary/attendance', moduleId: 'salary_attendance' },
        { name: 'Attendance Settings', path: '/salary/attendance-settings', moduleId: 'salary_attendance_settings' },
      ]
    },
    {
      name: 'Fixed Assets',
      icon: FiBox,
      submenu: [
        { name: 'Asset Register', path: '/assets/register', moduleId: 'fixed_assets_register' },
        { name: 'Asset Categories', path: '/assets/categories', moduleId: 'fixed_assets_categories' },
        { name: 'Depreciation Run', path: '/assets/depreciation', moduleId: 'fixed_assets_depreciation' },
        { name: 'Asset Disposal', path: '/assets/disposal', moduleId: 'fixed_assets_disposal' },
        { name: 'Asset Appreciation', path: '/assets/appreciation', moduleId: 'fixed_assets_appreciation' },
      ]
    },
    {
      name: 'WhatsApp',
      icon: FiMessageCircle,
      submenu: [
        { name: 'Connect WhatsApp', path: '/whatsapp/connect', moduleId: 'whatsapp_connect' },
        { name: 'Message Templates', path: '/whatsapp/templates', moduleId: 'whatsapp_templates' },
        { name: 'Send Logs', path: '/whatsapp/logs', moduleId: 'whatsapp_logs' },
        { name: 'WhatsApp Settings', path: '/whatsapp/settings', moduleId: 'whatsapp_settings' },
      ]
    },
    {
      name: 'Reports',
      icon: FiBarChart2,
      submenu: [
        {
          name: 'Main Reports',
          isSubHeader: true,
          items: [
            { name: 'Journal Report', path: '/reports/journal', moduleId: 'reports_journal' },
            { name: 'Print Vouchers', path: '/reports/vouchers', moduleId: 'reports_vouchers' },
            { name: 'Serial Tracking', path: '/reports/serial-tracking', moduleId: 'reports_serial_tracking' },
            { name: 'PO Tracking', path: '/reports/po-tracking', moduleId: 'reports_po_tracking' },
            { name: 'SO Tracking', path: '/reports/so-tracking', moduleId: 'reports_so_tracking' },
            { name: 'GRN Tracking', path: '/reports/grn-tracking', moduleId: 'reports_grn_tracking' },
            { name: 'DC Tracking', path: '/reports/dc-tracking', moduleId: 'reports_dc_tracking' },
            { name: 'Draft Report', path: '/reports/drafts', moduleId: 'reports_drafts' },
            { name: 'User Activity Log', path: '/reports/user-activity', moduleId: 'reports_user_activity' },
          ]
        },
        {
          name: 'Purchase Reports',
          isSubHeader: true,
          items: [
            { name: 'Purchase Summary', path: '/reports/purchase-summary', moduleId: 'reports_purchase_summary' },
            { name: 'Purchase Register', path: '/reports/purchase-register', moduleId: 'reports_purchase_register' },
            { name: 'Party Purchase Summary', path: '/reports/party-purchase-summary', moduleId: 'reports_party_purchase_summary' },
            { name: 'Vendor Payments', path: '/reports/vendor-payments', moduleId: 'reports_vendor_payments' },
            { name: 'Purchase Activity', path: '/reports/purchase-activity', moduleId: 'reports_purchase_activity' },
            { name: 'Item Purchase Analysis', path: '/reports/item-purchase-analysis', moduleId: 'reports_item_purchase_analysis' },
            { name: 'Accounts Payable Aging', path: '/reports/ap-aging', moduleId: 'reports_ap_aging' },
            { name: 'Vendor Balances', path: '/reports/vendor-balances', moduleId: 'reports_vendor_balances' },
            { name: 'Landed Cost Analysis', path: '/reports/landed-cost-analysis', moduleId: 'reports_landed_cost_analysis' },
            { name: 'Purchase Cycle (Flow)', path: '/reports/purchase-cycle', moduleId: 'reports_purchase_cycle' },
          ]
        },
        {
          name: 'Sale Reports',
          isSubHeader: true,
          items: [
            { name: 'Sale Summary', path: '/reports/sale-summary', moduleId: 'reports_sale_summary' },
            { name: 'Sale Register', path: '/reports/sale-register', moduleId: 'reports_sale_register' },
            { name: 'POS Sales', path: '/reports/pos-sales', moduleId: 'reports_pos_sales' },
            { name: 'POS Shift / Z Report', path: '/reports/pos-shifts', moduleId: 'reports_pos_shifts' },
            { name: 'Branch Cash Position', path: '/reports/pos-branch-cash', moduleId: 'reports_pos_branch_cash' },
            { name: 'Bank Discount Report', path: '/reports/bank-discounts', moduleId: 'pos_bank_discounts' },
            { name: 'Party Sale Summary', path: '/reports/party-sale-summary', moduleId: 'reports_party_sale_summary' },
            { name: 'Collection Report', path: '/reports/collection-report', moduleId: 'reports_collection_report' },
            { name: 'Sale Activity', path: '/reports/sale-activity', moduleId: 'reports_sale_activity' },
            { name: 'Item Sale Analysis', path: '/reports/item-sale-analysis', moduleId: 'reports_item_sale_analysis' },
            { name: 'Accounts Receivable Aging', path: '/reports/ar-aging', moduleId: 'reports_ar_aging' },
            { name: 'Salesperson Performance', path: '/reports/salesperson-performance', moduleId: 'reports_salesperson_performance' },
            { name: 'Salesman Incentive', path: '/reports/salesman-incentive', moduleId: 'reports_salesman_incentive' },
            { name: 'Item-wise Profit & Loss', path: '/reports/item-profit-loss', moduleId: 'reports_item_profit_loss' },
            { name: 'Invoice-wise Profit & Loss', path: '/reports/invoice-profit-loss', moduleId: 'reports_invoice_profit_loss' },
            { name: 'Customer Balances', path: '/reports/customer-balances', moduleId: 'reports_customer_balances' },
            { name: 'Sales Intelligence', path: '/reports/sales-intelligence', moduleId: 'reports_sales_intelligence' },
            { name: 'Operational Metrics', path: '/reports/operational-metrics', moduleId: 'reports_operational_metrics' },
            { name: 'Sale Cycle (Flow)', path: '/reports/sale-cycle', moduleId: 'reports_sale_cycle' },
          ]
        },
        {
          name: 'Inventory Reports',
          isSubHeader: true,
          items: [
            { name: 'Inventory Ledger', path: '/reports/inventory-ledger', moduleId: 'reports_inventory_ledger' },
            { name: 'Inventory Balances', path: '/reports/inventory-balances', moduleId: 'reports_inventory_balances' },
            { name: 'Low Stock Alert', path: '/reports/low-stock-alert', moduleId: 'reports_low_stock_alert' },
            { name: 'Inventory Intelligence', path: '/reports/inventory-intelligence', moduleId: 'reports_inventory_intelligence' },
            { name: 'Stock Transfer Report', path: '/reports/stock-transfer', moduleId: 'reports_stock_transfer' },
            { name: 'Branch Transfer Report', path: '/reports/branch-transfer', moduleId: 'reports_branch_transfer' },
            { name: 'Transfer Report', path: '/reports/transfer-report', moduleId: 'reports_transfer_report' },
          ]
        },
        {
          name: 'Audit Reports',
          isSubHeader: true,
          items: [
            { name: 'Stock Audit List', path: '/reports/stock-audit-list', moduleId: 'reports_stock_audit_list' },
            { name: 'Count Difference Report', path: '/reports/count-difference', moduleId: 'reports_count_difference' },
            { name: 'Stock Loss Summary', path: '/reports/stock-loss', moduleId: 'reports_stock_loss' },
            { name: 'Most Missing Items', path: '/reports/missing-items', moduleId: 'reports_missing_items' },
          ]
        },
        {
          name: 'Financial Reports',
          isSubHeader: true,
          items: [
            { name: 'Ledger Report', path: '/reports/ledger', moduleId: 'reports_ledger' },
            { name: 'Trial Balance', path: '/reports/trial-balance', moduleId: 'reports_trial_balance' },
            { name: 'Profit & Loss', path: '/reports/profit-loss', moduleId: 'reports_profit_loss' },
            { name: 'Balance Sheet', path: '/reports/balance-sheet', moduleId: 'reports_balance_sheet' },
            { name: 'Cash Flow Statement', path: '/reports/cash-flow', moduleId: 'reports_cash_flow' },
            { name: 'Cash & Bank Balances', path: '/reports/cash-bank-balances', moduleId: 'reports_cash_bank_balances' },
            { name: 'Tax Reports', path: '/reports/tax-reports', moduleId: 'reports_tax_reports' },
            { name: 'Financial Health', path: '/reports/financial-health', moduleId: 'reports_financial_health' },
            { name: 'Cash Flow Management', path: '/reports/cash-flow-management', moduleId: 'reports_cash_flow_management' },
          ]
        },
        {
          name: 'Salary Reports',
          isSubHeader: true,
          items: [
            { name: 'Salary Register', path: '/reports/salary-register', moduleId: 'reports_salary_register' },
            { name: 'Staff Loan & Advance', path: '/reports/staff-loan-advance', moduleId: 'reports_staff_loan_advance' },
            { name: 'Statutory Contributions', path: '/reports/statutory-contributions', moduleId: 'reports_statutory_contributions' },
          ]
        },
        {
          name: 'Fixed Asset Reports',
          isSubHeader: true,
          items: [
            { name: 'Fixed Asset Register', path: '/reports/asset-register', moduleId: 'reports_asset_register' },
            { name: 'Depreciation Schedule', path: '/reports/depreciation-schedule', moduleId: 'reports_depreciation_schedule' },
            { name: 'FBR Depreciation Statement', path: '/reports/fbr-depreciation', moduleId: 'reports_fbr_depreciation' },
            { name: 'Asset Disposals', path: '/reports/asset-disposals', moduleId: 'reports_asset_disposals' },
            { name: 'Asset Movement', path: '/reports/asset-movement', moduleId: 'reports_asset_movement' },
          ]
        },
        {
          name: 'Job Reports',
          isSubHeader: true,
          items: [
            { name: 'Job Cost Sheet', path: '/reports/job-cost-sheet', moduleId: 'reports_job_cost_sheet' },
            { name: 'Job Profit & Loss', path: '/reports/job-profit-loss', moduleId: 'reports_job_profit_loss' },
          ]
        },
        {
          name: 'Production Reports',
          isSubHeader: true,
          items: [
            { name: 'Production Summary / Register', path: '/reports/production-summary', moduleId: 'reports_production_summary' },
            { name: 'Production Cost & Variance', path: '/reports/production-variance', moduleId: 'reports_production_variance' },
            { name: 'Material Issue Report', path: '/reports/material-issue', moduleId: 'reports_material_issue' },
            { name: 'Production Receipt Report', path: '/reports/production-receipt', moduleId: 'reports_production_receipt' },
          ]
        },
      ]
    },
  ];

  // A menu item is visible if:
  //   (a) its module is enabled for the company, AND
  //   (b) the user has permission to view the route (admin/owner bypass handled inside canViewRoute)
  const isVisible = (navItem) => {
    // SAAS-04: company data download is for the owner / admins only.
    if (navItem.adminOnly && !seeAllCounters) return false;
    if (navItem.moduleId && !isModuleEnabled(navItem.moduleId)) return false;
    if (navItem.path && !canViewRoute(navItem.path)) return false;
    return true;
  };

  // Filter menu items based on enabled modules and user permissions
  const filteredMenuItems = menuItems.map(item => {
    // Top-level item with direct path
    if (item.path) {
      return isVisible(item) ? item : null;
    }
    // Item with submenu
    if (item.submenu) {
      const filteredSubmenu = item.submenu
        .map(sub => {
          // Sub-header group (Reports)
          if (sub.isSubHeader) {
            const filteredItems = sub.items.filter(isVisible);
            if (filteredItems.length === 0) return null;
            return { ...sub, items: filteredItems };
          }
          // Regular submenu item
          return isVisible(sub) ? sub : null;
        })
        .filter(Boolean);
      if (filteredSubmenu.length === 0) return null;
      return { ...item, submenu: filteredSubmenu };
    }
    return item;
  }).filter(Boolean);

  // Bottom nav items (mobile only) — key sections for quick access.
  // Each item declares candidate paths; the first one the user can view is used.
  // Items with no accessible target are hidden entirely.
  const bottomNavCandidates = [
    { name: 'Home', icon: FiHome, paths: ['/dashboard'] },
    { name: 'Sales', icon: FiDollarSign, paths: ['/sales/sale-invoice', '/sales/non-tax-invoice', '/sales/quotation', '/sales/sale-order', '/sales/delivery-challan', '/sales/sale-return'] },
    { name: 'Purchases', icon: FiShoppingCart, paths: ['/purchases/purchase-invoice', '/purchases/non-tax-invoice', '/purchases/purchase-order', '/purchases/grn', '/purchases/purchase-return'] },
    { name: 'Reports', icon: FiBarChart2, paths: ['/reports/ledger', '/reports/trial-balance', '/reports/sale-summary', '/reports/purchase-summary', '/reports/journal'] },
  ];
  const bottomNavItems = [
    ...bottomNavCandidates
      .map(c => {
        const path = c.paths.find(p => canViewRoute(p));
        return path ? { name: c.name, icon: c.icon, path } : null;
      })
      .filter(Boolean),
    { name: 'More', icon: FiMenu, action: 'toggle-sidebar' },
  ];

  const isActive = (path) => location.pathname === path;
  const isParentActive = (submenu) => {
    if (!submenu) return false;
    return submenu.some(item => {
      if (item.items) {
        return item.items.some(subItem => location.pathname === subItem.path);
      }
      return location.pathname === item.path;
    });
  };

  const hasSubHeaders = (submenu) => {
    return submenu && submenu.some(item => item.isSubHeader);
  };

  // Check if a bottom nav item's section is active
  const isBottomNavActive = (item) => {
    if (item.action) return sidebarOpen;
    if (item.path === '/dashboard') return location.pathname === '/dashboard';
    // Match section prefix
    const section = item.path.split('/')[1]; // e.g. 'sales', 'purchases', 'reports'
    return location.pathname.startsWith(`/${section}`);
  };

  const handleBottomNavClick = (item) => {
    if (item.action === 'toggle-sidebar') {
      setSidebarOpen(prev => !prev);
    } else {
      goTo(item.path);
      closeSidebar();
    }
  };

  // ── Render mobile accordion submenu ──
  const renderMobileSubmenu = (menuItem, menuIndex) => {
    if (!menuItem.submenu) return null;
    const isExpanded = expandedMenu === menuIndex;
    if (!isExpanded) return null;

    if (hasSubHeaders(menuItem.submenu)) {
      // Nested groups (e.g. Reports)
      return (
        <AccordionContent>
          {menuItem.submenu.map((group, gIdx) => (
            <div key={gIdx}>
              <AccordionSubHeader
                onClick={(e) => handleSubTriggerClick(gIdx, e)}
                $expanded={expandedSubMenu === gIdx}
              >
                <span>{group.name}</span>
                <FiChevronDown
                  size={13}
                  style={{
                    transform: expandedSubMenu === gIdx ? 'rotate(180deg)' : 'rotate(0)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              </AccordionSubHeader>
              {expandedSubMenu === gIdx && (
                <AccordionSubContent>
                  {group.items.map((subItem, sIdx) => (
                    <AccordionItem
                      key={sIdx}
                      href={subItem.path || '#'}
                      $active={isActive(subItem.path)}
                      onClick={(e) => handleFlyoutItemClick(subItem.path, e)}
                      onAuxClick={(e) => e.stopPropagation()}
                      onContextMenu={(e) => e.stopPropagation()}
                    >
                      {subItem.name}
                    </AccordionItem>
                  ))}
                </AccordionSubContent>
              )}
            </div>
          ))}
        </AccordionContent>
      );
    }

    // Simple list
    return (
      <AccordionContent>
        {menuItem.submenu.map((subItem, sIdx) => (
          <AccordionItem
            key={sIdx}
            href={subItem.path || '#'}
            $active={isActive(subItem.path)}
            onClick={(e) => handleFlyoutItemClick(subItem.path, e)}
            onAuxClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.stopPropagation()}
          >
            {subItem.name}
          </AccordionItem>
        ))}
      </AccordionContent>
    );
  };

  return (
    <Container>
      {/* App-wide loader for any transaction save/delete (blocks double-clicks) */}
      <GlobalBusyOverlay />

      {/* Overlay backdrop (mobile only) */}
      {isMobile && sidebarOpen && (
        <Overlay onClick={closeSidebar} />
      )}

      <Sidebar ref={sidebarRef} $isOpen={sidebarOpen} $isMobile={isMobile}>
        <SidebarHeader>
          <LogoBrandingBlock>
            <SwitcherLogoImg src={activeCompany?.brandLogoUrl || SwitcherLogo} alt={activeCompany?.brandLogoUrl ? activeCompany.companyName : 'Switcher Techno'} />
            <Logo>{activeCompany?.brandName || 'AMAL ERP'}</Logo>
          </LogoBrandingBlock>
          <CloseBtn onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            <FiX size={20} />
          </CloseBtn>
        </SidebarHeader>

        <Nav>
          {filteredMenuItems.map((item, index) => (
            <MenuGroup
              key={index}
              ref={el => menuGroupRefs.current[index] = el}
              onClick={() => handleMenuGroupClick(index, item)}
            >
              <NavItem
                $active={isActive(item.path) || isParentActive(item.submenu)}
                $hasSubmenu={!!item.submenu}
              >
                <NavItemLeft>
                  <item.icon />
                  <span>{item.name}</span>
                </NavItemLeft>
                {item.submenu && (
                  isMobile ? (
                    <FiChevronDown
                      size={14}
                      style={{
                        transform: expandedMenu === index ? 'rotate(180deg)' : 'rotate(0)',
                        transition: 'transform 0.2s ease'
                      }}
                    />
                  ) : (
                    <FiChevronRight size={14} />
                  )
                )}
              </NavItem>

              {/* Mobile: accordion submenu inline */}
              {isMobile && renderMobileSubmenu(item, index)}

              {/* Desktop: flyout submenu */}
              {!isMobile && item.submenu && (
                <Flyout data-flyout $isOpen={openMenuIndex === index}>
                  {hasSubHeaders(item.submenu) ? (
                    item.submenu.map((group, gIdx) => (
                      <FlyoutSubTrigger
                        key={gIdx}
                        data-sub-trigger
                        onClick={(e) => handleSubTriggerClick(gIdx, e)}
                      >
                        <FlyoutItem $hasChildren>
                          <span>{group.name}</span>
                          <FiChevronRight size={13} />
                        </FlyoutItem>
                        <SubFlyout data-sub-flyout $isOpen={openMenuIndex === index && openSubMenuIndex === gIdx}>
                          {group.items.map((subItem, sIdx) => (
                            <FlyoutItem
                              key={sIdx}
                              href={subItem.path || '#'}
                              $active={isActive(subItem.path)}
                              onClick={(e) => handleFlyoutItemClick(subItem.path, e)}
                              onAuxClick={(e) => e.stopPropagation()}
                              onContextMenu={(e) => e.stopPropagation()}
                            >
                              <span>{subItem.name}</span>
                            </FlyoutItem>
                          ))}
                        </SubFlyout>
                      </FlyoutSubTrigger>
                    ))
                  ) : (
                    item.submenu.map((subItem, sIdx) => (
                      <FlyoutItem
                        key={sIdx}
                        href={subItem.path || '#'}
                        $active={isActive(subItem.path)}
                        onClick={(e) => handleFlyoutItemClick(subItem.path, e)}
                        onAuxClick={(e) => e.stopPropagation()}
                        onContextMenu={(e) => e.stopPropagation()}
                      >
                        <span>{subItem.name}</span>
                      </FlyoutItem>
                    ))
                  )}
                </Flyout>
              )}
            </MenuGroup>
          ))}
        </Nav>

        <SidebarFooter>
          <UserBlock>
            <UserName>{userProfile?.displayName || userProfile?.fullName || 'User'}</UserName>
            <UserRole>
              {activeCompany?.companyName || 'No Company'}
              {companyUser?.role ? ` - ${companyUser.role.charAt(0).toUpperCase() + companyUser.role.slice(1)}` : ''}
            </UserRole>
          </UserBlock>
          <LogoutBtn onClick={handleLogout} title="Logout">
            <FiLogOut />
          </LogoutBtn>
        </SidebarFooter>
      </Sidebar>

      <MainContent $isMobile={isMobile} $sidebarOpen={sidebarOpen}>
        <Header>
          <HeaderLeft>
            {(!sidebarOpen || isMobile) && (
              <HamburgerBtn onClick={() => setSidebarOpen(true)} aria-label="Open menu">
                <FiMenu size={22} />
              </HamburgerBtn>
            )}
            <HeaderTitles>
              <PageTitle>{pageTitle}</PageTitle>
              <Breadcrumb>{breadcrumb}</Breadcrumb>
            </HeaderTitles>
          </HeaderLeft>
          <HeaderRight>
            {headerActions}
            {isBranchModuleEnabled() && (
              <BranchSelect
                value={activeBranch?.id || ''}
                onChange={(e) => switchBranch(e.target.value)}
              >
                {companyUser?.role === 'admin' && (
                  <option value="all">All Branches</option>
                )}
                {(companyUser?.role === 'admin'
                  ? branches
                  : branches.filter(b => (companyUser?.assignedBranches || []).includes(b.id))
                ).map(b => (
                  <option key={b.id} value={b.id}>{b.branchName}</option>
                ))}
              </BranchSelect>
            )}
            {(activeCompany?.replicateTo || activeCompany?.replicateFrom) && (
              <BooksToggle>
                <BooksOption
                  $active={!activeCompany?.replicateFrom}
                  onClick={() => {
                    if (activeCompany?.replicateFrom) {
                      switchCompany(activeCompany.replicateFrom);
                    }
                  }}
                >
                  Real Books
                </BooksOption>
                <BooksOption
                  $active={!!activeCompany?.replicateFrom}
                  onClick={() => {
                    if (activeCompany?.replicateTo) {
                      switchCompany(activeCompany.replicateTo);
                    }
                  }}
                >
                  Tax Books
                </BooksOption>
              </BooksToggle>
            )}
            {!isMobile && (
              <>
                <WhatsAppStatusBadge />
                <SubscriptionTag company={activeCompany} onOpen={canSeeSubscription ? () => setShowSubDrawer(true) : null} />
                <UserInfo>
                  <UserInfoName>{userProfile?.displayName || userProfile?.fullName || 'User'}</UserInfoName>
                  <UserInfoRole>
                    {activeCompany?.companyName || 'No Company'}
                    {companyUser?.role ? ` • ${companyUser.role.charAt(0).toUpperCase() + companyUser.role.slice(1)}` : ''}
                  </UserInfoRole>
                </UserInfo>
                {canAskAI && (
                  <AskAIButton onClick={openAI} title="Ask AI Assistant">
                    <FiStar />
                    <span>Ask AI</span>
                  </AskAIButton>
                )}
                <LogoutButton onClick={handleLogout}>
                  <FiLogOut />
                  <span>Logout</span>
                </LogoutButton>
              </>
            )}
          </HeaderRight>
        </Header>

        <Content $isMobile={isMobile}>
          {children}
        </Content>
      </MainContent>

      {/* Mobile bottom navigation */}
      {isMobile && (
        <BottomNav>
          {bottomNavItems.map((item, idx) => (
            <BottomNavItem
              key={idx}
              $active={isBottomNavActive(item)}
              onClick={() => handleBottomNavClick(item)}
            >
              <item.icon size={20} />
              <BottomNavLabel>{item.name}</BottomNavLabel>
            </BottomNavItem>
          ))}
        </BottomNav>
      )}

      {/* Trial Subscription Popups */}
      <AnimatePresence>
        {trialPopup && (
          <PopupOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleCloseTrialPopup}
          >
            <PopupContainer
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              <PopupCloseButton onClick={handleCloseTrialPopup}>
                <FiX size={20} />
              </PopupCloseButton>
              <PopupImage
                src={SubscriptionFeaturesImg}
                alt="Subscription Features"
              />
            </PopupContainer>
          </PopupOverlay>
        )}
      </AnimatePresence>

      {/* Trial-expired popup — shown to expired-trial users on login; closeable */}
      <AnimatePresence>
        {expiredPopup && (
          <PopupOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpiredPopup(false)}
          >
            <PopupContainer
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 30 }}
              transition={{ type: 'spring', duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              <PopupCloseButton onClick={() => setExpiredPopup(false)} aria-label="Close">
                <FiX size={20} />
              </PopupCloseButton>
              <ExpHeader>
                <ExpBell><FiBell size={36} /></ExpBell>
                <ExpTitle>Your Free Trial Has Expired</ExpTitle>
                {activeCompany?.companyName && <ExpCompany>{activeCompany.companyName}</ExpCompany>}
              </ExpHeader>
              <ExpBodyWrap>
                <ExpMsg>
                  Your 7-day free trial of AmalERP has ended. To keep using the software and continue
                  your services without interruption, please activate your subscription.
                </ExpMsg>
                <ExpWarn>
                  <FiAlertTriangle size={20} style={{ flex: 'none', marginTop: 1 }} />
                  <div><strong>Important:</strong> if your account is not activated, it may be suspended and your data could be lost.</div>
                </ExpWarn>
                <ExpContact>
                  <ExpContactLabel>Call or WhatsApp us to activate your account</ExpContactLabel>
                  <ExpContactNumber href="tel:+923353058349">+92 335 3058349</ExpContactNumber>
                </ExpContact>
                <ExpBtns>
                  <ExpiredWhatsApp href={SUPPORT_WA_URL} target="_blank" rel="noopener noreferrer">
                    <FiMessageCircle size={18} /> WhatsApp Now
                  </ExpiredWhatsApp>
                  <ExpLaterBtn type="button" onClick={() => setExpiredPopup(false)}>Remind Me Later</ExpLaterBtn>
                </ExpBtns>
              </ExpBodyWrap>
            </PopupContainer>
          </PopupOverlay>
        )}
      </AnimatePresence>

      <AIAssistant />

      <MySubscriptionDrawer open={showSubDrawer} onClose={() => setShowSubDrawer(false)} company={activeCompany} />
    </Container>
  );
};

// ── Styled Components ──

const SIDEBAR_WIDTH = layout.sidebarWidth;

const Container = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${colors.background.paper};
`;

// ── Overlay (mobile sidebar backdrop) ──
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 150;
  animation: ${fadeIn} 0.2s ease;
`;

// ── Flyout (Level 1) ── desktop only, uses fixed positioning
const Flyout = styled.div`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  left: ${SIDEBAR_WIDTH};
  min-width: 210px;
  background: ${colors.secondary.main};
  border: 1px solid ${colors.secondary.light};
  border-radius: ${borderRadius.md};
  box-shadow: 4px 4px 16px rgba(0, 0, 0, 0.35);
  padding: 4px 0;
  z-index: 200;
  flex-direction: column;
  max-height: 80vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary.light};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// ── Sub-Flyout (Level 2) ── desktop only
const SubFlyout = styled.div`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  min-width: 210px;
  background: ${colors.secondary.main};
  border: 1px solid ${colors.secondary.light};
  border-radius: ${borderRadius.md};
  box-shadow: 4px 4px 16px rgba(0, 0, 0, 0.35);
  padding: 4px 0;
  z-index: 210;
  flex-direction: column;
  max-height: 80vh;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary.light};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

// ── Flyout Item ──
const FlyoutItem = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 14px;
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.white};
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s ease;
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  text-decoration: none;

  &:hover {
    background: ${props => props.$active ? colors.primary.main : colors.primary.dark};
    color: ${colors.text.white};
  }

  &:focus { outline: none; }

  > span {
    flex: 1;
  }

  > svg {
    margin-left: 12px;
    opacity: 0.6;
    flex-shrink: 0;
  }
`;

// ── Sub-trigger wrapper (for nested flyouts) ──
const FlyoutSubTrigger = styled.div`
  position: relative;
`;

// ── Menu Group (wraps parent item + its flyout/accordion) ──
const MenuGroup = styled.div`
  position: relative;
`;

// ── Mobile Accordion Styles ──
const AccordionContent = styled.div`
  padding-left: 16px;
  border-left: 2px solid ${colors.primary.main};
  margin-left: 18px;
  margin-top: 2px;
  margin-bottom: 4px;
`;

const AccordionItem = styled.a`
  display: block;
  padding: 8px 12px;
  font-size: ${typography.fontSize.xs};
  color: ${props => props.$active ? colors.primary.contrast : 'rgba(255, 255, 255, 0.8)'};
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: background 0.15s ease;
  text-decoration: none;

  &:hover {
    color: ${props => props.$active ? colors.primary.contrast : 'white'};
  }

  &:active {
    background: ${colors.primary.dark};
  }
`;

const AccordionSubHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  color: ${props => props.$expanded ? colors.primary.contrast : 'rgba(255, 255, 255, 0.7)'};
  background: ${props => props.$expanded ? 'rgba(128, 0, 32, 0.3)' : 'transparent'};
  border-radius: ${borderRadius.sm};
  cursor: pointer;

  &:active {
    background: rgba(128, 0, 32, 0.4);
  }
`;

const AccordionSubContent = styled.div`
  padding-left: 8px;
  margin-top: 2px;
`;

// ── Sidebar ──
const slideIn = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(0); }
`;

const Sidebar = styled.aside`
  width: ${SIDEBAR_WIDTH};
  background: ${colors.secondary.main};
  color: ${colors.text.white};
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  height: 100vh;
  box-shadow: ${shadows.md};
  z-index: 100;
  overflow-y: auto;

  /* Desktop: use left positioning (not transform) to preserve fixed flyouts */
  left: ${props => props.$isOpen ? '0' : `-${SIDEBAR_WIDTH}`};
  transition: left 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${colors.secondary.light};
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }

  /* Mobile: slide-in drawer with transform */
  ${props => props.$isMobile && css`
    width: 280px;
    z-index: 200;
    left: 0;
    transform: ${props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: ${props.$isOpen ? '4px 0 24px rgba(0, 0, 0, 0.3)' : 'none'};
  `}
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px ${spacing.md};
  border-bottom: 1px solid ${colors.secondary.light};
  flex-shrink: 0;
`;

const LogoBrandingBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const SwitcherLogoImg = styled.img`
  width: 100%;
  max-width: 140px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
`;

const Logo = styled.h1`
  font-size: ${typography.fontSize.h5};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.white};
  letter-spacing: 1px;
`;

const CloseBtn = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:active {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: ${spacing.sm} ${spacing.sm};
  flex: 1;
`;

const NavItemLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  flex: 1;
  min-width: 0;

  > span {
    font-size: ${typography.fontSize.sm};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.xs};
  padding: 9px 10px;
  border-radius: ${borderRadius.sm};
  cursor: pointer;
  transition: background 0.15s ease;
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  color: ${colors.text.white};

  &:hover {
    background: ${props => props.$active ? colors.primary.main : colors.secondary.light};
  }

  /* Larger touch targets on mobile */
  ${media.sm} {
    padding: 12px 10px;
  }

  > svg:first-child {
    font-size: 17px;
    min-width: 17px;
    flex-shrink: 0;
  }

  > svg:last-child {
    opacity: 0.5;
    flex-shrink: 0;
  }

  > ${NavItemLeft} > span {
    font-weight: ${props => props.$active ? typography.fontWeight.medium : typography.fontWeight.regular};
  }
`;

const SidebarFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-top: 1px solid ${colors.secondary.light};
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  background: ${colors.secondary.main};
`;

const UserBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const UserName = styled.p`
  font-size: 11px;
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.p`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LogoutBtn = styled.button`
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: ${colors.primary.main};
    color: ${colors.text.white};
  }

  svg {
    font-size: 16px;
  }
`;

// ── Main Content ──
const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  margin-left: ${props => (!props.$isMobile && props.$sidebarOpen) ? SIDEBAR_WIDTH : '0'};
  transition: margin-left 0.3s ease;

  /* Account for bottom nav on mobile */
  ${props => props.$isMobile && css`
    padding-bottom: ${layout.bottomNavHeight};
  `}
`;

const Header = styled.header`
  background: ${colors.background.default};
  padding: ${spacing.md} ${spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: ${shadows.sm};
  position: sticky;
  top: 0;
  z-index: 100;

  ${media.sm} {
    padding: ${spacing.sm} ${spacing.md};
    gap: ${spacing.sm};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  min-width: 0;
  flex: 1;
`;

const HeaderTitles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const HamburgerBtn = styled.button`
  background: transparent;
  color: ${colors.text.primary};
  padding: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};
  flex-shrink: 0;

  &:active {
    background: ${colors.neutral[200]};
  }
`;

const PageTitle = styled.h2`
  font-size: ${typography.fontSize.h4};
  color: ${colors.text.primary};
  font-weight: ${typography.fontWeight.semibold};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${media.sm} {
    font-size: ${typography.fontSize.lg};
  }
`;

const Breadcrumb = styled.p`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};

  ${media.sm} {
    display: none;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.lg};
  position: relative;
  flex-shrink: 0;

  ${media.sm} {
    gap: ${spacing.sm};
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const UserInfoName = styled.p`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
`;

const UserInfoRole = styled.p`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const aiGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(124,58,237,0.55), 0 2px 10px rgba(0,0,0,0.12); }
  50%      { box-shadow: 0 0 16px 5px rgba(236,72,153,0.55), 0 2px 10px rgba(0,0,0,0.12); }
`;
const aiShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
const aiSparkle = keyframes`
  0%, 100% { transform: rotate(0deg) scale(1); opacity: 1; }
  50%      { transform: rotate(18deg) scale(1.18); opacity: 0.85; }
`;

const AskAIButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.md};
  background: linear-gradient(135deg, #7C3AED 0%, #DB2777 55%, #F59E0B 100%);
  background-size: 220% 220%;
  color: #fff;
  border: none;
  border-radius: 999px;
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  animation: ${aiGlow} 2.2s ease-in-out infinite, ${aiShift} 6s ease infinite;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px) scale(1.05); }

  svg {
    font-size: 15px;
    animation: ${aiSparkle} 2.2s ease-in-out infinite;
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.md};
  background: transparent;
  color: ${colors.primary.main};
  border: 1px solid ${colors.primary.main};
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.primary.main};
    color: ${colors.text.white};
  }

  svg {
    font-size: 14px;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
  overflow-y: auto;

  ${props => props.$isMobile && css`
    padding: ${spacing.md} ${spacing.sm};
    gap: ${spacing.md};
  `}
`;

// ── Bottom Navigation (mobile) ──
const BottomNav = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${layout.bottomNavHeight};
  background: ${colors.background.default};
  border-top: 1px solid ${colors.border.light};
  display: flex;
  align-items: center;
  justify-content: space-around;
  z-index: 100;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
`;

const BottomNavItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 4px 0;
  background: transparent;
  color: ${props => props.$active ? colors.primary.main : colors.text.secondary};
  flex: 1;
  min-width: 0;
  transition: color 0.15s ease;

  &:active {
    color: ${colors.primary.main};
  }

  svg {
    flex-shrink: 0;
  }
`;

const BottomNavLabel = styled.span`
  font-size: 10px;
  font-weight: ${typography.fontWeight.medium};
  white-space: nowrap;
`;

const SubTag = styled.span`
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  ${props => props.$type === 'trial' && css`
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
  `}

  ${props => props.$type === 'expired' && css`
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `}

  ${props => props.$type === 'active' && css`
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
  `}
`;

const BranchSelect = styled.select`
  padding: 6px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 13px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  max-width: 180px;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const BooksToggle = styled.div`
  display: flex;
  gap: 2px;
  background: ${colors.neutral[100]};
  border-radius: 8px;
  padding: 3px;
  border: 1px solid ${colors.neutral[200]};
`;

const BooksOption = styled.button`
  padding: 5px 12px;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  background: ${props => props.$active ? colors.primary.main : 'transparent'};
  color: ${props => props.$active ? 'white' : colors.text.secondary};

  &:hover {
    background: ${props => props.$active ? colors.primary.main : colors.neutral[200]};
  }
`;

// ── Trial Popup & WhatsApp Styled Components ──

const SubTagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const WhatsAppBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #25D366;
    color: white;
    border-color: #25D366;
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const PopupContainer = styled.div`
  position: relative;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  max-width: 520px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.3);
`;

const PopupCloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.75);
    transform: scale(1.1);
  }
`;

const PopupImage = styled.img`
  width: 100%;
  max-height: 75vh;
  object-fit: contain;
  display: block;
`;

// Trial-expired popup (2026-10-30)
const ExpHeader = styled.div`
  background: linear-gradient(135deg, ${colors.primary.main}, #5c0017);
  color: white;
  text-align: center;
  padding: 30px 30px 26px;
`;
const ExpBell = styled.div`
  width: 74px;
  height: 74px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.14);
  color: #FFC107;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ExpTitle = styled.h2`
  margin: 0;
  font-size: 25px;
  font-weight: 700;
  color: white;
`;
const ExpCompany = styled.p`
  margin: 6px 0 0;
  font-size: 14px;
  opacity: 0.85;
`;
const ExpBodyWrap = styled.div`
  padding: 24px 30px 26px;
  background: white;
  overflow-y: auto;
`;
const ExpMsg = styled.p`
  font-size: 15px;
  color: #333;
  line-height: 1.55;
  margin: 0 0 16px;
  text-align: center;
`;
const ExpWarn = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fdecee;
  border: 1px solid #f5c2c7;
  border-radius: 10px;
  padding: 13px 15px;
  color: #842029;
  font-size: 14px;
  line-height: 1.45;
  margin-bottom: 18px;
`;
const ExpContact = styled.div`
  border: 1px dashed #d9b3bc;
  border-radius: 12px;
  text-align: center;
  padding: 14px 10px;
  margin-bottom: 20px;
`;
const ExpContactLabel = styled.div`
  font-size: 13px;
  color: #666;
`;
const ExpContactNumber = styled.a`
  display: inline-block;
  font-size: 28px;
  font-weight: 700;
  color: ${colors.primary.main};
  letter-spacing: 0.5px;
  margin-top: 4px;
  text-decoration: none;
`;
const ExpBtns = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  & > * { flex: 1 1 0; min-width: 160px; justify-content: center; box-sizing: border-box; }
`;
const ExpLaterBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  border: 1px solid #ccc;
  border-radius: 10px;
  background: white;
  color: #444;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  &:hover { background: #f5f5f5; }
`;

const ExpiredWhatsApp = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  background: #25D366;
  color: white;
  font-size: 15px;
  font-weight: 700;
  border-radius: 10px;
  text-decoration: none;
  transition: all 0.2s;
  &:hover { background: #1da851; transform: translateY(-1px); }
`;

export default PageLayout;
